import React, { useCallback, useMemo } from 'react';
import { View, ScrollView } from 'react-native';
import { useTranslation } from '@oolio-group/localization';
import { makeStyles } from './NetworkDevicesModal.styles';
import { useTheme } from '../../../hooks/useTheme';
import NetworkDeviceRow from './NetworkDeviceRow';
import Empty from '../../Empty/Empty';
import Message from '../../Message/Message';
import ModalTitle from '../ModalTitle/ModalTitle';
import TreatButton from '../../TreatButton/TreatButton';
import { useLocalDeviceStatus } from '../../../utils/localComms/hooks/useLocalDeviceStatus';
import { networkStatusSubject } from '../../../hooks/connectivity/useNetworkListener';
import useBehaviorSubjectState from '../../../hooks/rxjs/useSubjectState';
import { localComms } from '../../../utils/localComms';

export interface NetworkDevicesModalProps {
  onClose: () => void;
}
const NetworkDevicesModal: React.FC<NetworkDevicesModalProps> = ({
  onClose,
}) => {
  const { translate } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const { value: isConnected } = useBehaviorSubjectState(networkStatusSubject);

  const sdkStatus = useLocalDeviceStatus();

  const onRestartClicked = async () => {
    await localComms.restart();
  };
  const onExportLogClicked = async () => {
    localComms.shareLog();
    onCloseModal();
  };

  const onExportDbClicked = async () => {
    localComms.exportDatabase();
    onCloseModal();
  };

  const onCloseModal = useCallback(() => {
    onClose();
  }, [onClose]);

  const convertTimeStamp = useCallback((timestamp: string) => {
    const dateTimePart =
      timestamp.split(' ')[0] + 'T' + timestamp.split(' ')[1];
    const date = new Date(dateTimePart + 'Z');
    const options: Intl.DateTimeFormatOptions = {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };
    return date.toLocaleString('en-US', options);
  }, []);

  return (
    <View style={styles.container}>
      <ModalTitle
        title={translate('settings.posDevices')}
        onDismiss={onCloseModal}
      />
      <View style={styles.content}>
        {(sdkStatus?.queue || 0 > 0) && (
          <Message
            testID="message-queueCount"
            type="negative"
            icon="UilExclamationTriangle"
            message={translate('settings.syncQueue', {
              count: sdkStatus?.queue || 0,
            })}
            containerStyle={styles.message}
          />
        )}
        {sdkStatus?.nodes?.length ?? 0 > 0 ? (
          <ScrollView style={styles.list}>
            <View style={styles.rows}>
              {sdkStatus?.nodes?.map((device, i) => (
                <NetworkDeviceRow
                  key={i}
                  name={device.metadata?.name ?? device.nodeId}
                  lastSeen={convertTimeStamp(device.lastSeen)}
                  isAvailableInLocalNetwork={
                    sdkStatus.isLocalCommunicationAvailable === true
                      ? device.currentDevice === true
                        ? sdkStatus.isConnected === true &&
                          device.status === 'UP'
                        : device.status === 'UP'
                      : false
                  }
                  hasInternetConnection={
                    device.currentDevice === true ? isConnected : true
                  }
                  ipAddress={device.nodeId}
                  isCurrentDevice={device.currentDevice === true}
                  isCDSDevice={device.metadata?.appType === 'CDS'}
                />
              ))}
            </View>
          </ScrollView>
        ) : (
          <Empty label={translate('DeviceSelection.noDevices')} />
        )}
        <View style={styles.actions}>
          <TreatButton
            testID="btn-exportLogs"
            label={translate('button.exportLogs')}
            type="neutralLight"
            onPress={onExportLogClicked}
          />
          <TreatButton
            testID="btn-exportDbToBackup"
            label={translate('button.exportDbToBackup')}
            type="neutralLight"
            onPress={onExportDbClicked}
          />
          <TreatButton
            testID="btn-refreshServer"
            label={translate('button.refreshServer')}
            type="neutral"
            onPress={onRestartClicked}
          />
        </View>
      </View>
    </View>
  );
};

export default NetworkDevicesModal;
